import React, { useEffect, useRef, useState } from 'react';
import { useRouter } from "next/router";
import { usePlayer } from "@/lib/usePlayer";
import { useUpdateDaily } from "@/lib/usePlayer";
import toast from "react-hot-toast";
import Page from "@/components/layout/Page";
import DailyQuestion from "@/components/daily/DailyQuestion";
import Summary from "@/components/daily/Summary";
import Loading from "@/components/common/Loading";
import { dailyToPieMask, sendGtagEvent } from "@/lib/utils";
import {
	STANDARD_CATEGORIES,
	STANDARD_CATEGORIES_BY_KEY,
	TOAST_DURATION, DAILY_TIMER_LIMIT
} from "@/lib/constants.mjs";
import { DAILY_GAME_STATES } from "@hasbro/tpi-data-models";
import { getPublicConfig } from '@/pages/api/config';
import { DateTime, Settings } from "luxon";
Settings.defaultZone = 'America/New_York'

const CLOCK_TICK = 0;
const PLAYER_UI_URL = getPublicConfig('PLAYER_UI_URL');

const getCategory = category => STANDARD_CATEGORIES.find(c => c.label === category)

const Daily = () => {
  const { data: player, isSuccess } = usePlayer();
	const { mutateAsync: updateDaily } = useUpdateDaily();

  const { uid } = player || {};
	const { category } = player?.daily || {};

	const [clock, setClock] = useState({isTimerRunning: false})
	const [timeRemaining, setTimeRemaining] = useState(0)
	const interval = useRef()
	const router = useRouter();

	const stopTimer = () => {
		clearInterval(interval.current)
		setClock(value => ({...value, isTimerRunning: false}))
	}

	// initial visit state load
	useEffect(() => {
		const onPlay = async () => {
			sendGtagEvent({
				action: "daily_start_click",
				category: "engagement",
				label: "Daily Start Click",
				value: "click",
				user_id: uid,
			});
			await updateDaily({
        uid: player?.uid,
        body: {
          action: "start",
          ...(router.query?.preview && { preview: router.query.preview }),
      }});
      setTimeRemaining(DAILY_TIMER_LIMIT);
      setClock((value) => ({
        ...value,
        startTime: Date.now(),
        isTimerRunning: player.daily.state !== DAILY_GAME_STATES.Question,
      }));
		};
		if (player?.daily?.state === DAILY_GAME_STATES.Ready) {
			onPlay();
		}
	}, [player?.daily?.state]);

  // clock management
  useEffect(() => {
    if (clock?.isTimerRunning) {
      interval.current = setInterval(() => {
        const elapsed = Date.now() - clock.startTime;
        const remaining = DAILY_TIMER_LIMIT - elapsed;
        setTimeRemaining(remaining);
        if (remaining <= 0 && player?.daily.state !== DAILY_GAME_STATES.Summary) {
          stopTimer();
          onUserChoice('');
        }
      }, CLOCK_TICK);
    } else if (interval.current) {
      clearInterval(interval.current);
    }
    return () => {
      if (interval.current) clearInterval(interval.current);
    };
  }, [clock]);
	
  // refresh & navigation handling
  useEffect(() => {
    const exitingFunction = () => {
      setClock({ isTimerRunning: true, startTime: Date.now() - (DAILY_TIMER_LIMIT + 100) });
    };
    // call exit function when we navigate away
    router.events.on("routeChangeStart", exitingFunction);
    // call exit function when we refresh
    window.onbeforeunload = exitingFunction;
  }, []);

	const onUserChoice = async choice => {
		if (clock.isTimerRunning) {
      sendGtagEvent({
        action: "daily_choice_click",
        category: "engagement",
        label: "Daily Choice Click",
        value: "click",
        user_id: uid,
      });
      await updateDaily({
        uid: player?.uid, 
        body: {
          action: "choice",
          category,
          choice,
          ...(router.query?.preview && { preview: router.query.preview }),
      }});
      setClock((value) => ({
        ...value,
        startTime: Date.now(),
        isTimerRunning: player.daily.state !== DAILY_GAME_STATES.Question,
      }));
		}
	}

  const onNext = async () => {
    sendGtagEvent({
      action: "daily_next_click",
      category: "engagement",
      label: "Daily Next Click",
      value: "click",
      user_id: uid,
    });
    await updateDaily({
      uid: player?.uid,
      body: {
        action: "next",
        ...(router.query?.preview && { preview: router.query.preview }),
    }});
    setTimeRemaining(DAILY_TIMER_LIMIT);
    setClock((value) => ({
      ...value,
      startTime: Date.now(),
      isTimerRunning: player.daily.state !== DAILY_GAME_STATES.Question,
    }));
  };


	const onShare = async (event) => {
    event.stopPropagation();
    sendGtagEvent({
      action: "daily_challenge_share",
      category: "engagement",
      label: "Daily Challenge Share",
      value: "click",
      user_id: uid,
    });
    const data = player?.daily;
    if (data) {
      const dateStr = DateTime.fromISO(data.lastAction).toLocaleString();
      const emojiString = [
        STANDARD_CATEGORIES_BY_KEY.entertainment.emoji,
        STANDARD_CATEGORIES_BY_KEY.geography.emoji,
        STANDARD_CATEGORIES_BY_KEY.sports.emoji,
        STANDARD_CATEGORIES_BY_KEY.science.emoji,
        STANDARD_CATEGORIES_BY_KEY.history.emoji,
        STANDARD_CATEGORIES_BY_KEY.arts.emoji,
        "\n",
        data.wedges.entertainment.today ? "✅" : "❌",
        data.wedges.geography.today ? "✅" : "❌",
        data.wedges.sports.today ? "✅" : "❌",
        data.wedges.science.today ? "✅" : "❌",
        data.wedges.history.today ? "✅" : "❌",
        data.wedges.arts.today ? "✅" : "❌",
      ].join("");
      const content = {
        text: `Trivial Pursuit Infinite
DAILY CHALLENGE - ${dateStr}
Score: ${data?.collectedToday}/6▼
${emojiString}`,
        url: `${PLAYER_UI_URL}/daily?utm_source=tpi-game&utm_medium=share`,
      };
      if (navigator?.share && navigator.canShare(content) && navigator.maxTouchPoints) {
        navigator.share(content);
      } else {
        // desktop/alternate sharing. copy to clipboard
        navigator.clipboard.writeText(`${content.text}\n${content.url}`).then(
          () => { // success
            toast.success("Copied to clipboard 👍", { duration: TOAST_DURATION });
          },
          () => { // failure
            toast.error("Hmm. Could not Copy to clipboard");
          }
        );
      }
      return true;
    }
  };

  const onLoaderCancel = () => {
    sendGtagEvent({
      action: "daily_loader_cancel",
      category: "engagement",
      label: "Daily Loader Cancel",
      value: "click",
      user_id: uid,
    });
    stopTimer();
    router.push("/");
  };

  const onQuit = () => {
    sendGtagEvent({
      action: "daily_quit_click",
      category: "engagement",
      label: "Daily Quit Click",
      value: "click",
      user_id: uid,
    });
    stopTimer();
    router.push("/");
  };

	if (isSuccess) {
		switch (player?.daily.state) {
			case DAILY_GAME_STATES.Question:
			case DAILY_GAME_STATES.Answer:
				const { color, key } = getCategory(player.daily.category);
				return (
					<Page mode={key} topAd={true} pageTitle={"Daily Trivia Game"}>
						<DailyQuestion
							timeTotal={DAILY_TIMER_LIMIT}
							timeRemaining={timeRemaining}
							isTimerRunning={clock.isTimerRunning}
							question={player.daily.question}
							choices={player.daily.choices}
              gameModeTitle={"Daily Challenge"}
							category={player.daily.category}
							categoryColor={color}
							categoryMask={dailyToPieMask(player.daily?.wedges)}
							userChoice={player.daily.userChoice}
							answer={player.daily.answer}
							onUserChoice={onUserChoice}
							onNext={onNext}
							secondaryButtonLabel="Quit"
							onQuit={onQuit}
							isLastQuestion={player.daily.history.length >= 6}
						/>
					</Page>
				);
			case DAILY_GAME_STATES.Summary:
				return (
					<Page pageTitle={"Daily Challenge Summary"}>
						<Summary
							data={player.daily}
							onShare={onShare}
							onQuit={onQuit}
						/>
					</Page>
				);
		}
	}

  return (
    <Page pageTitle="Daily Trivia Game">
      <Loading onCancel={onLoaderCancel} />
    </Page>
  );
};

export default Daily;
